import { useCallback, useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';

import { IReportConfig, TicketSearched } from 'api/ticket';
import { formatImagesAllow, toJpgBase64List } from 'pages/Ticket/Detail/utils';
import {
  ItilBulkExportWizard,
  fetchItilTranslationPreferApi,
  getCloseItilText,
  ITIL_TRANSLATION_TYPE_BY_SCOPE,
  type BulkExportSummary,
  type BulkExportVersion
} from 'features/itilGenius';
import { useApiContext } from 'context/ApiContext';

import ResultModal from './ResultModal';
import getPdfTemplate from '../pdfTemplate/getPdfTemplate';
import useTicketsExportApis from '../hooks/Service/useTicketsExportApis';

type Props = {
  ticketsData: TicketSearched[];
};

const validateFormatImagesAllow = (url: string) => {
  let urlFormat = '';
  formatImagesAllow.forEach((format) => {
    if (url?.toLocaleLowerCase().includes(format)) urlFormat = url;
  });
  return urlFormat;
};

function ExportPDFZipButton({ ticketsData }: Props) {
  const [ticketsIds, setTicketsIds] = useState<{ ticketId: number } | null>(
    null
  );
  const pdfData = useRef<
    { fileName: string; docDefinition: unknown; usedItil: boolean }[]
  >([]);
  const ticketIndex = useRef<number>(0);
  const ticketArray = useRef<{ ticketId: number }[]>([]);
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const [isDownloadingData, setIsDownloadingData] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const loadingTicketDataStatus = useRef<string>('Descargando datos...');
  const { t } = useTranslation('global');
  const { itilGenius } = useApiContext();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardTicketIds, setWizardTicketIds] = useState<number[]>([]);
  const [exportVersion, setExportVersion] = useState<BulkExportVersion>('itil');
  const [missingItilIds, setMissingItilIds] = useState<number[]>([]);
  const [bulkProgress, setBulkProgress] = useState<{
    current: number;
    total: number;
    ticketId?: number;
  } | null>(null);
  const [bulkSummary, setBulkSummary] = useState<BulkExportSummary | null>(
    null
  );

  const {
    clientsDetail,
    clientProjectDetail,
    location,
    images,
    supplies,
    closeTicketComents,
    getBase,
    GetComments,
    ticket,
    isTicketDetailLoading,
    locationIsLoading,
    GetCommentsLoading,
    reportConfig
  } = useTicketsExportApis(ticketsIds);

  const resolvePdfOverrides = useCallback(
    async (ticketId: number) => {
      if (exportVersion === 'original') {
        return undefined;
      }

      const useOriginal = missingItilIds.includes(ticketId);
      if (useOriginal) {
        return undefined;
      }

      const descriptionItil = await fetchItilTranslationPreferApi(
        itilGenius,
        ticketId,
        'description',
        ITIL_TRANSLATION_TYPE_BY_SCOPE.description
      );

      const fromSearch = ticketsData.find((t) => t.ticketId === ticketId)
        ?.itilTranslatedCloseComment;
      const fromClosedDetails =
        ticketId === closeTicketComents?.data?.ticketId ||
        ticketId === ticket?.ticketId
          ? getCloseItilText(
              closeTicketComents?.data?.commentDetails,
              closeTicketComents?.data?.itilTranslatedCloseComment
            )
          : null;
      const closingItilFromApi = await fetchItilTranslationPreferApi(
        itilGenius,
        ticketId,
        'closing',
        ITIL_TRANSLATION_TYPE_BY_SCOPE.closing
      );
      const closingItil =
        fromSearch?.trim() || fromClosedDetails || closingItilFromApi;

      if (!descriptionItil && !closingItil) {
        return undefined;
      }

      return {
        ...(descriptionItil
          ? { activitiesDescription: descriptionItil }
          : {}),
        ...(closingItil ? { closeComments: closingItil } : {})
      };
    },
    [
      exportVersion,
      missingItilIds,
      itilGenius,
      ticketsData,
      closeTicketComents?.data?.commentDetails,
      closeTicketComents?.data?.itilTranslatedCloseComment,
      closeTicketComents?.data?.ticketId,
      ticket?.ticketId
    ]
  );

  const extractTicketsId = () => {
    const ids = ticketsData.map((item) => ({
      ticketId: item.ticketId
    }));
    ticketArray.current = ids;
    setWizardTicketIds(ids.map((item) => item.ticketId));
    setWizardOpen(true);
  };

  const startExport = (
    version: BulkExportVersion,
    missing: number[] = []
  ) => {
    setExportVersion(version);
    setMissingItilIds(missing);
    ticketIndex.current = 0;
    pdfData.current = [];
    setBulkSummary(null);
    setBulkProgress({
      current: 0,
      total: ticketArray.current.length,
      ticketId: ticketArray.current[0]?.ticketId
    });
    setTicketsIds(ticketArray.current[0] ?? null);
    setOpenStatusModal(true);
    setIsDownloadingData(true);
    loadingTicketDataStatus.current = t('itilGenius.bulkExport.progressLabel', {
      current: 1,
      total: ticketArray.current.length
    });
    ticketIndex.current = 1;
  };

  useEffect(() => {
    const exportTickets = async () => {
      if (
        !isTicketDetailLoading &&
        !clientsDetail.isLoading &&
        !clientProjectDetail.isLoading &&
        !locationIsLoading &&
        !images.isLoading &&
        !supplies.isLoading &&
        !closeTicketComents.isLoading &&
        !GetCommentsLoading &&
        !reportConfig.isLoading &&
        ticketsIds
      ) {
        try {
          await handleExportPDF();
        } catch (err) {
          console.error('SAIMONOPE-752: error preparando PDF del ticket', ticketsIds, err);
          setIsDownloadingData(false);
          setIsSuccess(false);
          setBulkProgress(null);
          return;
        }
        if (ticketIndex.current < ticketArray.current.length) {
          const next = ticketArray.current[ticketIndex.current];
          setTicketsIds(next);
          setBulkProgress({
            current: ticketIndex.current,
            total: ticketArray.current.length,
            ticketId: next.ticketId
          });
          loadingTicketDataStatus.current = t(
            'itilGenius.bulkExport.progressLabel',
            {
              current: ticketIndex.current + 1,
              total: ticketArray.current.length
            }
          );
          ticketIndex.current++;
        } else {
          exportZip();
          ticketIndex.current = 0;
          setTicketsIds(null);
        }
      }
    };

    void exportTickets();
  }, [
    isTicketDetailLoading,
    clientsDetail.isLoading,
    clientProjectDetail.isLoading,
    locationIsLoading,
    images.isLoading,
    supplies.isLoading,
    closeTicketComents.isLoading,
    GetCommentsLoading,
    reportConfig.isLoading,
    ticketsIds
  ]);

  const handleExportPDF = async () => {
    const pdfImages = images?.data
      ?.map((url: { blobStorageUrlPath?: string }) =>
        validateFormatImagesAllow(url?.blobStorageUrlPath ?? '')
      )
      ?.filter((elem) => elem !== '');

    // SAIMONOPE-752: igual que export simple — JPEG vía canvas.
    // pdfMake con URL cruda falla en PNG truncados (p. ej. 64 KB sin IEND) y el ZIP queda colgado.
    const pdfImagesBase64 = await toJpgBase64List(pdfImages ?? []);

    const base64Logo = await getBase();

    let config: IReportConfig = null;
    if (reportConfig?.data?.reportConfig) {
      const dataJSON = JSON.parse(
        reportConfig?.data?.reportConfig as string
      ) as IReportConfig;
      config = { ...dataJSON };
    }

    const pdfOverrides = await resolvePdfOverrides(ticket?.ticketId);
    const usedItil = !!pdfOverrides?.activitiesDescription;

    pdfData.current.push({
      fileName: `${t('ticket.pdf.pdfName')}-${ticket?.ticketId}.pdf`,
      usedItil,
      docDefinition: getPdfTemplate(
        {
          clientActive: clientsDetail?.data,
          projectAndService: clientProjectDetail?.data,
          dataIncident: ticket,
          comments: GetComments?.data?.items,
          location,
          componentLocation: [],
          supplies: supplies?.data,
          closedTicketComments: closeTicketComents?.data,
          pdfOverrides,
          reportConfig:
            config ||
            ({
              client: clientsDetail?.data?.name,
              requested: ticket?.requestedByUser
            } as IReportConfig)
        },
        pdfImagesBase64,
        images?.data?.map((item) => item?.description),
        base64Logo
      )
    });
  };

  const exportZip = () => {
    if (ticketArray.current.length === pdfData.current.length) {
      const zip = new JSZip();
      const { pdfMake } = window;

      function addPdfToZip(
        zipRef: JSZip,
        fileName: string,
        docDefinition: unknown
      ) {
        return new Promise<number>((resolve, reject) => {
          try {
            pdfMake.createPdf(docDefinition).getBlob((blob: Blob) => {
              zipRef.file(fileName, blob);
              resolve(1);
            });
          } catch (err) {
            reject(err instanceof Error ? err : new Error(String(err)));
          }
        });
      }

      Promise.all(
        pdfData.current.map((pdf) =>
          addPdfToZip(zip, pdf.fileName, pdf.docDefinition)
        )
      )
        .then(() => {
          zip.generateAsync({ type: 'blob' }).then((content) => {
            saveAs(content, 'Detalle_de_tickets.zip');
            const itilCount = pdfData.current.filter((p) => p.usedItil).length;
            setBulkSummary({
              total: pdfData.current.length,
              itilCount,
              originalCount: pdfData.current.length - itilCount
            });
            pdfData.current = [];
            setIsDownloadingData(false);
            setIsSuccess(true);
            setBulkProgress(null);
          });
        })
        .catch((err) => {
          console.error('Error generating PDFs:', err);
          setIsDownloadingData(false);
          setIsSuccess(false);
          setBulkProgress(null);
        });
    } else {
      setIsDownloadingData(false);
      setIsSuccess(false);
      setBulkProgress(null);
    }
  };

  const handleClose: (
    event: object,
    reason?: string
  ) => void = (_, reason) => {
    if (reason && reason === 'backdropClick') return;
    setOpenStatusModal(false);
  };

  return (
    <>
      <ItilBulkExportWizard
        onClose={() => {
          setWizardOpen(false);
          setBulkSummary(null);
        }}
        onConfirmExport={(version, missing) => {
          setWizardOpen(false);
          startExport(version, missing);
        }}
        open={wizardOpen}
        progress={bulkProgress ?? undefined}
        summary={bulkSummary}
        ticketIds={wizardTicketIds}
      />

      <ResultModal
        acceptChanges={handleClose}
        isLoading={isDownloadingData}
        onClose={handleClose}
        open={openStatusModal}
        statusDescription={loadingTicketDataStatus.current}
        success={isSuccess}
      />

      <Button
        disabled={!ticketsData?.length}
        onClick={extractTicketsId}
        variant="contained"
      >
        {t('ticket.properties.downloadPDF')}
      </Button>
    </>
  );
}

export default ExportPDFZipButton;
